import { GameState } from './GameState';
import { endorsementSystem } from './EndorsementSystem';
import { EventBus, GameEvents } from '../core/EventBus';
import { GameConfig } from '../core/GameConfig';
import { GameData } from '../data/GameData';
import { NormalNpc } from '../data/types';
import { PERSONALITY_TEMPLATES, PersonalityKey } from '../data/tables/NormalNpcData';
import { GIFT_MAP, GiftCategory, GiftDef } from '../data/tables/Gifts';

/**
 * 社交系统
 * 管理聊天/邀约/送礼等 NPC 互动
 *
 * 定稿规则（V2）：NPC 分两类，统一文案解析
 *  - 特殊 NPC：GameData.NPC_MAP（原版 NPC，不参与竞争）
 *  - 普通 NPC：开局随机生成，按性格模板共享文案池
 *  - 统一先查特殊 NPC，查不到再按普通 NPC 性格模板取
 *  - 结识：初始 -1 未结识（不显示于人脉页），通告拍摄周偶遇 VN 选「友好」后置 0 起步
 *  - 好感度等级阈值（43 批五档）：0 初识 / 20 认识 / 40 熟悉 / 60 好友 / 80 挚友（爱心一格一档）
 *  - 聊天：免费，好感固定 +2（文案由 NPC 池的 chatTexts 随机提供，无选项）
 *  - 邀约（选周自动赴约 2026-10-28，原 2026-10-11 预约制，设计=docs/自由出行与小地图-设计方案.md §六）：
 *    人脉页发起即掷成功率（好感 ≥40、成功率=好感/100、月限发起时扣）——婉拒=好感+0 不占周末；
 *    答应=append 进 player.pendingInvites（多槽，数组顺序=挂约顺序；周级互斥=一周只一个自由日行程；`week` 未定=约未成形），玩家在聊天页选定本月第几周后到点自动赴约
 *    （+INVITE_WEEKEND_GAIN，见 FreeroamSystem.resolvePendingInvite）；`week` 未定整月=收月作废、月限不退
 *  - 送礼（2026-10-11 道具化改版）：送背包已有的礼物（giftId），好感=gift.favor（送中喜好 ×2），
 *    回礼照旧（≥60 好友 20% / ≥80 挚友 35% 概率回 30% 价格）；⛔ 花钱当场买
 */

/** 聊天结果（text = 本次展示的 NPC 聊天文案） */
export interface ChatResult { ok: boolean; gain: number; text?: string; }
/** 邀约结果（选周制：success=对方答应挂起之约；gain 恒 0——好感在赴约周末自动演出时才结算） */
export interface InviteResult { ok: boolean; success: boolean; gain: number; reason?: string; }
/** 送礼结果（道具版） */
export interface GiftResult { ok: boolean; gain: number; isFavorite: boolean; backMoney: number; reason?: string; }

/** 统一 NPC 文案包装（特殊 NPC 或普通 NPC 性格模板） */
interface NpcTexts {
    chatTexts: string[];
    inviteOkText: string;
    inviteFailText: string;
    giftTexts: string[];
    /** 赴约见面对话：普通 NPC = 池（随机抽一条），特殊 NPC = 单条（inviteMeetText） */
    inviteMeetTexts?: string[];
    inviteMeetText?: string;
}

export class SocialSystem {
    private static instance: SocialSystem;

    private constructor() { }

    static getInstance(): SocialSystem {
        if (!this.instance) {
            this.instance = new SocialSystem();
        }
        return this.instance;
    }

    /**
     * 统一 NPC 文案解析器
     * 先在 GameData.NPC_MAP（特殊 NPC）中查找，查不到再按普通 NPC 的 personality 性格模板取
     * 完全找不到时返回 undefined
     */
    getNpcTexts(npcId: string): NpcTexts | undefined {
        // ① 特殊 NPC
        const special = GameData.NPC_MAP[npcId];
        if (special) {
            return {
                chatTexts: special.chatTexts,
                inviteOkText: special.inviteOkText,
                inviteFailText: special.inviteFailText,
                giftTexts: special.giftTexts,
                inviteMeetText: special.inviteMeetText,
            };
        }

        // ② 普通 NPC
        const state = GameState.getInstance();
        const normalNpc = state.player.normalNpcs.find(n => n.id === npcId);
        if (normalNpc) {
            const template = PERSONALITY_TEMPLATES[normalNpc.personality as PersonalityKey];
            if (template) {
                return {
                    chatTexts: template.chatTexts,
                    inviteOkText: template.inviteOkText,
                    inviteFailText: template.inviteFailText,
                    giftTexts: template.giftTexts,
                    inviteMeetTexts: template.inviteMeetTexts,
                };
            }
        }

        return undefined;
    }

    /** 获取 NPC 显示名称（统一解析） */
    getNpcName(npcId: string): string {
        const special = GameData.NPC_MAP[npcId];
        if (special) return special.name;
        const state = GameState.getInstance();
        const normal = state.player.normalNpcs.find(n => n.id === npcId);
        if (normal) return normal.name;
        return npcId;
    }

    /** 获取当前月剩余聊天次数 */
    getRemainingChats(): number {
        const state = GameState.getInstance();
        return GameConfig.CHAT_LIMIT - state.player.socialCounts.chat;
    }

    /** 获取当前月剩余邀约次数 */
    getRemainingInvites(): number {
        const state = GameState.getInstance();
        return GameConfig.INVITE_LIMIT - state.player.socialCounts.invite;
    }

    /**
     * 聊天（免费，好感固定 +2，返回本次展示的聊天对话供 UI 渲染）
     * 对话由 NPC 文案池 chatTexts 随机取一段（主角开场|NPC回复）；未结识 NPC 不可聊天
     * V2 统一文案解析，支持普通 NPC 性格模板
     */
    chat(npcId: string): ChatResult {
        const state = GameState.getInstance();

        // 检查次数限制
        if (state.player.socialCounts.chat >= GameConfig.CHAT_LIMIT) {
            return { ok: false, gain: 0 };
        }

        // 检查 NPC 是否已结识
        const npcName = this.getNpcName(npcId);
        if (state.getFavor(npcId) < 0) return { ok: false, gain: 0 };

        // 聊天免费，无体力/金钱消耗

        // 好感固定 +2
        const favorGain = 2;
        state.addFavor(npcId, favorGain);

        // 情商微增（聊天轻量成长）
        state.addAttribute('eq', 1);

        // 记录次数
        state.player.socialCounts.chat++;

        // 统一文案解析
        const texts = this.getNpcTexts(npcId);
        const text = texts?.chatTexts[Math.floor(Math.random() * texts.chatTexts.length)]
            ?? '最近在忙什么呀？|最近怎么样，找时间聚聚？';

        return { ok: true, gain: favorGain, text };
    }

    /**
     * 邀约（选周自动赴约 2026-10-28）：人脉页发起 → 即时成功率判定（好感 ≥40、成功率=好感/100、月限发起时扣）。
     * 婉拒=好感+0（原「失败+1」作废）、不占周末；答应=挂起 player.pendingInvite（`week` 未定=约未成形），
     * 玩家在聊天页选定本周几（setInviteWeek）后到选定周的周末自动赴约、+INVITE_WEEKEND_GAIN 结算。
     * 多槽（2026-10-29）：不同 NPC 可挂在不同周；周级互斥（一周只一个自由日行程）；同一 NPC 只挂一个（UI 置灰，这里再兜一道）。
     */
    invite(npcId: string): InviteResult {
        const state = GameState.getInstance();
        const p = state.player;

        // 检查次数限制
        if (p.socialCounts.invite >= GameConfig.INVITE_LIMIT) {
            return { ok: false, success: false, gain: 0, reason: '本月邀约次数已用完' };
        }

        // 同一 NPC 同时只挂一个（多槽批 2026-10-29 茄子定「加闸」）；不同 NPC 可并存、不必等上一场赴完
        if ((p.pendingInvites ?? []).some(i => i.npcId === npcId)) {
            const held = this.getNpcName(npcId);
            return { ok: false, success: false, gain: 0, reason: `已与 ${held} 有约` };
        }

        const npcName = this.getNpcName(npcId);
        if (state.getFavor(npcId) < 0) return { ok: false, success: false, gain: 0 };

        // 好感度门槛：熟悉（40，43 批五档并轨后 31→40 对齐档位线）
        const favor = state.getFavor(npcId);
        if (favor < 40) {
            return { ok: false, success: false, gain: 0, reason: `好感度 ${favor}，需 ≥40 才能邀约` };
        }

        // 成功率 = 好感度/100（原型同款：上限 90%）
        const success = Math.random() * 100 < Math.min(90, favor);

        // 记录次数（答应/婉拒都扣；整月未定周次的作废不退——防「约了不办」白嫖）
        p.socialCounts.invite++;

        if (!success) {
            // 婉拒：好感 +0（2026-10-11 拍板），不占周末
            return { ok: true, success: false, gain: 0 };
        }

        // 答应：追加挂起之约（week 未定，等玩家在聊天页选周；好感在赴约周末自动演出时结算）
        if (!Array.isArray(p.pendingInvites)) p.pendingInvites = [];
        p.pendingInvites.push({ npcId });
        return { ok: true, success: true, gain: 0 };
    }

    /**
     * 本周是否已有自由日行程（周级互斥，2026-10-29 茄子令「一周只能安排一次，不管邀约/飞行/后面加的别的」）：
     * 读多槽 `pendingInvites`——飞行等后续行程只要挂同一数组，占用判据自动覆盖。
     * `exceptNpcId` 排除自己那一条（选周时本 NPC 的挂起项 week 未定，正常不会命中）。
     */
    isWeekOccupied(week: number, exceptNpcId?: string): boolean {
        const list = GameState.getInstance().player.pendingInvites ?? [];
        if (list.some(i => i.week === week && i.npcId !== exceptNpcId)) return true;
        // 综艺录制占自由日（商务代言批 2026-10-30；2026-11-02 起品牌挚友改正常工作行程，不再计入占用）
        const p = GameState.getInstance().player;
        const abs = p.year * 12 + p.month;
        return endorsementSystem.occupiedEventWeeks(abs).includes(week);
    }

    /**
     * 选定赴约周次（选周自动化 2026-10-28；多槽批 2026-10-29 改按 NPC 定位）：把该 NPC 名下
     * 未定周次的挂起之约钉到本月第 `week` 周（1~4），到该周周末由 MonthProgressPanel 自动演出。
     * 无约/已定周次=幂等闸不动。
     */
    setInviteWeek(npcId: string, week: number): void {
        const p = GameState.getInstance().player;
        if (this.isWeekOccupied(week, npcId)) return;   // 周级互斥兜底（UI 已置灰，这里再兜一道）
        const inv = (p.pendingInvites ?? []).find(i => i.npcId === npcId && !i.week);
        if (!inv) return;
        inv.week = week;
        // 不落盘（月存口径 2026-11-03）：选定的周次等月初那一格捎带
    }

    /**
     * NPC 的送礼喜好类别（自由出行批）：特殊 NPC 读 SpecialNpcDef.favCategory（数据批未落=undefined），
     * 普通 NPC 按性格模板（cheerful→零食 / quiet→文创 / sharp→饰品 / warm→护肤，草案）。
     * 探知机制挂起（2026-10-11 茄子令）：本结果⛔ 直出 UI，只参与好感 ×2 判定。
     */
    favoriteCategoryOf(npcId: string): GiftCategory | undefined {
        const sp = GameData.SPECIAL_NPCS.find(s => s.npcId === npcId);
        if (sp) return sp.favCategory;
        const state = GameState.getInstance();
        const normal = state.player.normalNpcs.find(n => n.id === npcId);
        if (normal) {
            return PERSONALITY_TEMPLATES[normal.personality as PersonalityKey]?.favCategory;
        }
        return undefined;
    }

    /** 这件礼物送给这位 NPC 的实际好感（送中喜好 ×2，2026-10-11 拍板） */
    giftFavorFor(npcId: string, gift: GiftDef): number {
        const fav = this.favoriteCategoryOf(npcId);
        return fav && fav === gift.category ? gift.favor * 2 : gift.favor;
    }

    /**
     * 送礼（道具版 2026-10-11）：送背包已有的礼物（⛔ 花钱当场买），好感 = gift.favor（送中喜好 ×2），
     * 回礼照旧（好友 20% / 挚友 35% 概率回 30% 礼物价格——回的是钱不是物）。
     * 不限月次数（价签+库存已是成本）；偶遇 VN 的送礼格走 EncounterSystem.resolve 的 gift 分支（同一套 favor 口径）。
     */
    gift(npcId: string, giftId: string): GiftResult {
        const state = GameState.getInstance();
        const p = state.player;
        const gift = GIFT_MAP[giftId];
        const isFavorite = !!gift && this.favoriteCategoryOf(npcId) === gift.category;

        const npcName = this.getNpcName(npcId);
        if (state.getFavor(npcId) < 0) return { ok: false, gain: 0, isFavorite, backMoney: 0, reason: '还未结识' };
        if (!gift) return { ok: false, gain: 0, isFavorite, backMoney: 0, reason: '查无此礼物' };
        if (state.getItemCount(giftId) <= 0) {
            return { ok: false, gain: 0, isFavorite, backMoney: 0, reason: `背包里没有${gift.name}了` };
        }

        // 扣库存
        state.takeItem(giftId);

        // 好感：基准档 ×喜好翻倍
        const favorGain = this.giftFavorFor(npcId, gift);

        // 好友20% / 挚友35% 回礼（回 30% 礼物价格，回的是钱）
        const favor = state.getFavor(npcId);
        const backRate = favor >= 80 ? 0.35 : (favor >= 60 ? 0.2 : 0);
        let backMoney = 0;
        let backText = '';
        if (Math.random() < backRate) {
            backMoney = Math.floor(gift.price * 0.3);
            state.addMoney(backMoney);
            backText = `，${npcName} 回赠了 ¥${backMoney}`;
        }

        state.addFavor(npcId, favorGain);

        return { ok: true, gain: favorGain, isFavorite, backMoney };
    }

    /** 删除好友（批次 C，方案 §三）：仅限随机（普通）NPC。好感回 -1 移出人脉页、
     *  清熟遇月限戳与招呼队列（红点一并消失）、回到偶遇池；同合同 castNpcIds 快照不动（在拍照拍）。
     *  ⚠️ 聊天记录本就只在会话内存活、从不落盘，删除无需额外处理 */
    removeFriend(npcId: string): boolean {
        const state = GameState.getInstance();
        const isNormal = state.player.normalNpcs.some(n => n.id === npcId);
        if (!isNormal || state.getFavor(npcId) < 0) return false;
        const name = this.getNpcName(npcId);
        state.player.relationships[npcId] = -1;
        if (state.player.encounterMetMonth) delete state.player.encounterMetMonth[npcId];
        state.player.greetings = state.player.greetings.filter(g => g.npcId !== npcId);
        return true;
    }

    /** 获取好感度等级描述（43 批五档：-1 未结识 / 0 初识 / 20 认识 / 40 熟悉 / 60 好友 / 80 挚友，与爱心一格一档同源） */
    getFavorLevel(npcId: string): { level: number; label: string } {
        const favor = GameState.getInstance().getFavor(npcId);
        if (favor < 0) return { level: 0, label: '未结识' };
        if (favor >= 80) return { level: 5, label: '挚友' };
        if (favor >= 60) return { level: 4, label: '好友' };
        if (favor >= 40) return { level: 3, label: '熟悉' };
        if (favor >= 20) return { level: 2, label: '认识' };
        return { level: 1, label: '初识' };
    }

    /** 获取全部已结识 NPC 好感度列表（含特殊 NPC + 普通 NPC） */
    getAllFavors(): { npcId: string; name: string; favor: number; level: string }[] {
        const state = GameState.getInstance();
        const result: { npcId: string; name: string; favor: number; level: string }[] = [];

        // 特殊 NPC
        for (const npc of GameData.NPCS) {
            const favor = state.getFavor(npc.id);
            if (favor >= 0) {
                result.push({ npcId: npc.id, name: npc.name, favor, level: this.getFavorLevel(npc.id).label });
            }
        }

        // 普通 NPC（已结识的才显示）；⛔ 特殊 NPC（竞争型实例）已在上面 NPCS 循环列出，跳过免得同一人重复
        for (const npc of state.player.normalNpcs) {
            if (GameData.NPC_MAP[npc.id]) continue;
            const favor = state.getFavor(npc.id);
            if (favor >= 0) {
                result.push({ npcId: npc.id, name: npc.name, favor, level: this.getFavorLevel(npc.id).label });
            }
        }

        // 按好感度降序排列
        result.sort((a, b) => b.favor - a.favor);
        return result;
    }

    /** 重置社交次数（每月初调用） */
    resetSocialCounts(): void {
        const state = GameState.getInstance();
        state.player.socialCounts = { chat: 0, invite: 0 };
    }
}

/** 导出单例 */
export const socialSystem = SocialSystem.getInstance();